/*
  Hasti Co-op Bank QR card for the NEW mold (HASTINewQRCard) - printed at 84.3mm x 133mm.
  HASTI has no old-mold card in this repo, so this is built directly from the
  artwork supplied:
    - /assets/HASTI/hasti-top.svg    (bank logo + tagline, raster-in-SVG)
    - /assets/HASTI/hasti-bottom.svg (BHIM/UPI + Paytm/PhonePe/GPay logos)
  The "Scan & Pay" pill and QR frame are plain CSS (no pill baked into the
  artwork here, unlike RSB's rsb-top.svg).
  Sized to match the other new-mold cards (same 380 x 600 px capture as CBINewQRCard).
*/
import React, { useRef } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import QRCodeGenerator from './QRCodeGenerator';
import FitMerchantName from './FitMerchantName';

const HASTI_ORANGE = '#ED7523';
const HASTI_BLUE = '#263D8A';

export default function HastiNewScanAndPayCard({ qrString, merchantVpa, merchantName }) {
  const cardRef = useRef(null);

  return (
    <Box
      ref={cardRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: HASTI_ORANGE,
        borderRadius: '32px',
        boxShadow: 3,
        p: '10px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      {/* Blue border - a diagonal cut, taller on the left (75% of the height)
          than on the right (25%), not a flat horizontal band. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          backgroundColor: HASTI_BLUE,
          clipPath: 'polygon(0% 25%, 100% 75%, 100% 100%, 0% 100%)'
        }}
      />
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          px: 2,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        {/* Bank logo + tagline */}
        <Box
          component="img"
          src="/assets/HASTI/hasti-top.svg"
          alt="Hasti Co-op Bank Ltd"
          sx={{ width: '100%', height: 'auto', flexShrink: 0, display: 'block' }}
        />

        {/* Merchant Name */}
        <FitMerchantName
          text={merchantName}
          maxFontSize={16}
          minFontSize={11}
          maxLines={2}
          lineHeight={1.2}
          fontWeight={700}
          color="black"
          containerSx={{ mt: 1.5, width: '100%' }}
        />

        {/* Scan & Pay pill */}
        <Box
          sx={{
            mt: 1,
            px: 3,
            py: 0.75,
            borderRadius: '20px',
            backgroundColor: HASTI_ORANGE,
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0
          }}
        >
          <Typography
            sx={{
              color: 'white',
              fontFamily: 'Montserrat',
              fontWeight: 700,
              fontSize: 18,
              letterSpacing: 0.5,
              lineHeight: 1
            }}
          >
            SCAN &amp; PAY
          </Typography>
        </Box>

        {/* QR Code - plain grey-bordered box */}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            border: '1.5px solid #9e9e9e',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0
          }}
        >
          <QRCodeGenerator qrString={qrString} size={208} />
        </Box>

        {/* UPI ID */}
        <Box sx={{ mt: 2, width: '90%', flexShrink: 0 }}>
          <Typography
            align="center"
            sx={{
              color: 'black',
              fontWeight: 600,
              fontFamily: 'Montserrat',
              fontSize: 14,
              lineHeight: 1.3,
              wordBreak: 'break-all',
              pb: 0.75
            }}
          >
            UPI ID : {merchantVpa}
          </Typography>
          <Divider sx={{ backgroundColor: 'black', height: '1.5px' }} />
        </Box>

        {/* Footer - BHIM/UPI and the accepted payment apps */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            mt: 2,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            component="img"
            src="/assets/HASTI/hasti-bottom.svg"
            alt="BHIM UPI accepted here"
            sx={{ width: '90%', maxHeight: '100%', height: 'auto', objectFit: 'contain', display: 'block' }}
          />
        </Box>
      </Box>
    </Box>
  );
}
